import { getDataRepository } from "../../../../HA";
import { adoptCsrfToken } from "../../Service/ResponseDispatcher";
import { onceI } from "./InteractionController";
import { enqueueSave, formSaveState, formVersion, isFormDirty, isSaveBusy, isSaveSuccess, markFormSaved } from "./FormSave";
import { isUnloadConsented, registerLeaveGuard } from "./LeaveGuard";

/** Au-delà, un vidage de sortie est un échec : l'utilisateur est consulté. */
export const LEAVE_FLUSH_TIMEOUT_MS = 10_000;

export const LEAVE_CONFIRM = "Modifications non enregistrées. Quitter quand même ?";

const mainidOf = (form: HTMLFormElement): string =>
    form.querySelector<HTMLInputElement>("input[name=\"mainid\"]")?.value ?? "";

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const t = window.setTimeout(() => reject(new Error("timeout")), ms);
        p.then((v) => { window.clearTimeout(t); resolve(v); }, (e) => { window.clearTimeout(t); reject(e); });
    });
}

/**
 * Envoie la saisie courante (édition seulement — jamais de création implicite). Résout `true`
 * si, au retour, plus rien n'est en attente : succès HTTP ET métier de la version envoyée.
 * La file reste occupée jusqu'à la fin RÉELLE de la requête (jamais libérée par un délai) :
 * un envoi suivant ne peut pas doubler un POST encore en vol.
 */
export function flushForm(form: HTMLFormElement): Promise<boolean> {
    return enqueueSave(form, async () => {
        if (!isFormDirty(form)) { return true; }  // un envoi précédent l'a couverte
        if (mainidOf(form) === "") { return false; }
        const version = formVersion(form);
        const fd = new FormData(form);
        const formId = form.getAttribute("data-ha-form-id");
        if (formId) { fd.set("formId", formId); }
        try {
            const response = await (getDataRepository().saveForm(fd) as Promise<Map<any, any>>);
            adoptCsrfToken(response);
            if (isSaveSuccess(response)) { markFormSaved(form, version); }
        } catch { /* réseau : la saisie reste à envoyer */ }
        return !isFormDirty(form);
    });
}

/** Garde de sortie : vider, sinon demander. Un refus laisse l'utilisateur sur sa saisie. */
async function settleOnLeave(form: HTMLFormElement): Promise<boolean> {
    if (!isFormDirty(form)) { return true; }
    // Le délai couvre l'attente en file (soumission manuelle pendante comprise) ET l'envoi :
    // la DÉCISION de sortie ne dépasse jamais LEAVE_FLUSH_TIMEOUT_MS ; la requête, elle,
    // continue et peut encore marquer la version sauvegardée.
    if (mainidOf(form) !== "" && await withTimeout(flushForm(form), LEAVE_FLUSH_TIMEOUT_MS).catch(() => false)) { return true; }
    return window.confirm(LEAVE_CONFIRM);
}

/**
 * data-ha-autosave — enregistrement silencieux périodique d'un stepform.
 *
 *   <form data-ha-stepform data-ha-form-id="…" data-ha-autosave="30">
 *
 * Toutes les N secondes, si le form a une saisie non sauvegardée ET un `mainid`, envoi
 * silencieux. Sortie SPA : garde (vidage, sinon confirmation). Sortie réelle (rechargement,
 * fermeture) : `beforeunload` — aucun envoi asynchrone n'y est garanti, l'avertissement seul.
 */
export function initAutosaves(root: ParentNode = document): void {
    root.querySelectorAll<HTMLFormElement>("form[data-ha-autosave]:not([data-ha-autosave-bound])").forEach((form) => {
        if (!onceI(form, "data-ha-autosave-bound")) { return; }
        const period = Math.max(10, parseInt(form.getAttribute("data-ha-autosave") || "30", 10)) * 1000;
        formSaveState(form);
        const unregister = registerLeaveGuard({ owner: form, isDirty: () => isFormDirty(form), settle: () => settleOnLeave(form) });
        const onBeforeUnload = (ev: BeforeUnloadEvent): void => {
            if (isUnloadConsented()) { return; }  // sortie déjà tranchée par la garde SPA
            if (form.isConnected && isFormDirty(form)) { ev.preventDefault(); ev.returnValue = ""; }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        const timer = window.setInterval(() => {
            if (!form.isConnected) {
                window.clearInterval(timer);
                window.removeEventListener("beforeunload", onBeforeUnload);
                unregister();
                return;
            }
            if (isFormDirty(form) && mainidOf(form) !== "" && !isSaveBusy(form)) { void flushForm(form); }
        }, period);
    });
}
